import { Button, Css } from "@homebound/beam";
import { useCallback } from "react";
import { useRouteParams } from "src/hooks/useRouteParams";
import { useProductOfferingVersionHistoryQuery } from "src/routes/developments/product-offerings/version-history/ProductOfferingVersionHistory.generated";
import { PageHeader } from "src/routes/layout/PageHeader";
import { VersionDetailsCard } from "src/routes/libraries/versioning/VersionDetailsCard";
import { VersionDetailsCard_VersionFragment } from "src/routes/libraries/versioning/VersionDetailsCard.generated";
import { createProductOfferingScopeUrl, createProductOfferingUrl } from "src/RouteUrls";
import { fail, queryResult } from "src/utils";
import { GeneralTlivChanges } from "../../../libraries/versioning/GeneralTlivChanges";
import { ProductTlivChanges } from "./components/ProductTlivChanges";

export function ProductOfferingVersionHistoryPage() {
  const { productOfferingId } = useRouteParams<{ productOfferingId: string }>();
  const query = useProductOfferingVersionHistoryQuery({
    variables: { filter: { readyPlanId: [productOfferingId] }, page: { limit: 10 } },
  });
  const maybeFetchMore = useCallback(async () => {
    if (query.data?.readyPlanAggregateVersionsPage.pageInfo.hasNextPage) {
      await query.fetchMore({
        variables: { page: { offset: query.data?.readyPlanAggregateVersionsPage.entities?.length, limit: 10 } },
      });
    }
  }, [query]);

  return queryResult(query, ({ readyPlanAggregateVersionsPage }) => (
    <ProductOfferingVersionHistoryView
      versions={readyPlanAggregateVersionsPage.entities}
      hasNextPage={readyPlanAggregateVersionsPage.pageInfo.hasNextPage}
      maybeFetchMore={maybeFetchMore}
    />
  ));
}

type PlanPackageVersionHistoryViewProps = {
  versions: VersionDetailsCard_VersionFragment[];
  hasNextPage: boolean;
  maybeFetchMore: () => Promise<void>;
};

function ProductOfferingVersionHistoryView(props: PlanPackageVersionHistoryViewProps) {
  const { productOfferingId, developmentId, projectId } = useRouteParams<{
    productOfferingId: string;
    developmentId: string;
    projectId: string;
  }>();
  const { versions, hasNextPage, maybeFetchMore } = props;
  // We reverse the array so that the most recent version is shown first.
  const rpavs = [...versions].reverse();
  const latestVersion = rpavs.first ?? fail("Expected at least one version");

  return (
    <>
      <PageHeader
        title="Version History"
        breadcrumb={[
          {
            href: createProductOfferingUrl(productOfferingId, latestVersion.id, developmentId, projectId),
            label: "Product Offering",
          },
          {
            href: createProductOfferingUrl(productOfferingId, latestVersion.id, developmentId, projectId),
            label: latestVersion.readyPlan.name,
          },
        ]}
      />
      <div css={Css.df.fdc.gap2.$}>
        {rpavs.reverse().map((rpav) => (
          <VersionDetailsCard
            key={rpav.id}
            version={rpav}
            changeDetails={<ProductOfferingVersionChanges version={rpav} />}
            versionUrl={createProductOfferingScopeUrl(productOfferingId, rpav.id, { developmentId, projectId })}
          />
        ))}
      </div>
      {hasNextPage && (
        <div css={Css.df.jcc.mt1.mb1.p2.$}>
          <Button label="Load More" onClick={maybeFetchMore} />
        </div>
      )}
    </>
  );
}

type ProductOfferingVersionChangesProps = { version: VersionDetailsCard_VersionFragment };

function ProductOfferingVersionChanges({ version }: ProductOfferingVersionChangesProps) {
  const { tliVersions } = version;
  return (
    <>
      <GeneralTlivChanges tliVersions={tliVersions} />
      <ProductTlivChanges tliVersions={tliVersions} />
    </>
  );
}
